import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {withRouter, type WithRouterProps} from 'react-router';

import {AcceptanceSidebar} from 'app/components/general/DetailPage/LeftSidebar/AcceptanceSidebar';
import {ActivitySidebar} from 'app/components/general/DetailPage/LeftSidebar/ActivitySidebar';
import {LeftMainMenuButton} from 'app/components/general/DetailPage/LeftSidebar/MainLeftSidebar';
import {PermissionsSidebar} from 'app/components/general/DetailPage/LeftSidebar/PermissionsSidebar';
import {PoliciesForProcedureLeftSidebar} from 'app/components/general/DetailPage/LeftSidebar/PoliciesLeftSidebar';
import {ProcessesForProcedureLeftSidebar} from 'app/components/general/DetailPage/LeftSidebar/ProcessesLeftSidebar';
import {ReviewSidebar} from 'app/components/general/DetailPage/LeftSidebar/ReviewSidebar';
import {SignoffLogSidebar} from 'app/components/general/DetailPage/LeftSidebar/SignOffLogSidebar';
import {TasksSidebar} from 'app/components/general/DetailPage/LeftSidebar/TasksSidebar';
import {TeamsSidebar} from 'app/components/general/DetailPage/LeftSidebar/TeamsSidebar';
import {ProcedureVersionsSidebar} from 'app/components/general/DetailPage/LeftSidebar/VersionsSidebar/VersionsSidebar';
import {DetailPageSidebarShortcuts} from 'app/general/shortcuts/DetailPageSidebarShortcuts';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {
  useProcedureByHashid,
  useProcedureVersionById,
} from 'app/procedures/api';
import {useToggleWidgetIsOpen} from 'app/settings/hooks';
import {COMMENTS_WIDGET} from 'app/settings/slice';
import {getObjectActivityURL, getObjectCurrentVersionURL} from 'app/urls';
import {getPathFromURL} from 'app/utils/urls';

import type {LeftSidebarProps} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {ProcedureHashidSerializedType} from 'sweet-types';

const leftSidebar = [
  ProcedureVersionsSidebar,
  AcceptanceSidebar,
  TasksSidebar,
  PoliciesForProcedureLeftSidebar,
  ProcessesForProcedureLeftSidebar,
  TeamsSidebar,
  PermissionsSidebar,
  ActivitySidebar,
  ReviewSidebar,
  SignoffLogSidebar,
];

function BaseProcedurePageSidebar({
  router,
  location,
  params,
  routeName,
  CurrentLeftSidebar,
}: LeftSidebarProps<ProcedureHashidSerializedType> & WithRouterProps) {
  const {versionid: versionId, hashid: hashId} = params;

  const {data: object, refetch} = useProcedureByHashid(hashId, {
    refetchOnMountOrArgChange: false,
  });
  const {data: version} = useProcedureVersionById(
    object == null
      ? skipToken
      : {versionId: versionId || object.current_version.id},
    {
      refetchOnMountOrArgChange: false,
    }
  );
  const toggleWidgetIsOpen = useToggleWidgetIsOpen();

  if (object == null) {
    return null;
  }

  const getVersionsURL = (): string => {
    return getPathFromURL(
      getObjectCurrentVersionURL(object, versionId, location.search)
    );
  };
  const getActivityURL = (): string => {
    return getObjectActivityURL(object, location.search);
  };
  const goToEditURL = () => {
    router.push(`${object.html_url}edit/`);
  };

  return (
    <DetailPageSidebarShortcuts
      openVersions={() => router.push(getVersionsURL())}
      openComments={() =>
        toggleWidgetIsOpen({
          contentType: 'procedure',
          objectId: object.id,
          widgetName: COMMENTS_WIDGET,
        })
      }
      openActivities={() => router.push(getActivityURL())}
      edit={goToEditURL}
    >
      <PageSidebarStructure
        innerContent={
          CurrentLeftSidebar && (
            <CurrentLeftSidebar.Panel
              object={object}
              version={version}
              legacyRefetch={refetch}
            />
          )
        }
      >
        {(showTooltip, hideTooltip) => (
          <>
            <LeftMainMenuButton
              key={'left-sidebar-menu-item-main'}
              object={object}
              forceTooltipShow={showTooltip}
              hideTooltip={hideTooltip}
              location={location}
              params={params}
            />
            {leftSidebar.map((Sidebar, index) => (
              <Sidebar.MenuButton
                key={`left-sidebar-menu-item-${index}`}
                active={routeName === Sidebar.sidebarName || false}
                object={object}
                forceTooltipShow={showTooltip}
                hideTooltip={hideTooltip}
                location={location}
                params={params}
              />
            ))}
          </>
        )}
      </PageSidebarStructure>
    </DetailPageSidebarShortcuts>
  );
}

export const ProcedurePageSidebar = withRouter<
  LeftSidebarProps<ProcedureHashidSerializedType>
>(BaseProcedurePageSidebar);
